<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAppToast } from '@/shared/lib/useAppToast'

import Card from "primevue/card";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { usePagination } from '@/shared/composables/usePagination'

import CategoryFormDialog from "@/modules/admin/categories/components/CategoryFormDialog.vue";
import DeleteCategoryConfirmDialog from "@/modules/admin/categories/components/DeleteCategoryConfirmDialog.vue";

import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "@/modules/admin/categories/api/categories.service";

const router = useRouter()
const toast = useAppToast()

const categories = ref([]);
const loading = ref(false);
const search = ref("");

const formVisible = ref(false);
const formMode = ref("create");
const selectedCategory = ref(null);

const deleteDialogVisible = ref(false);
const categoryToDelete = ref(null);

const submittingCategory = ref(false)
const deletingCategory = ref(false)
const categoryFieldErrors = ref({})

function getParentCategoryName(parentId) {
  if (!parentId) return 'Без родительской категории'

  const parent = categories.value.find(c => c.id === parentId)
  return parent?.name ?? 'Родительская категория не найдена'
}

async function loadCategories() {
  try {
    loading.value = true
    categories.value = await getAllCategories()
    console.log('Загруженные категории:', categories.value)
    categories.value.forEach(cat => {
      console.log(`Категория ${cat.name}: imageUrl =`, cat.imageUrl)
    })
  } catch (error) {
    categories.value = []
    toast.error(error.message || 'Не удалось загрузить категории')
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push("/admin");
}

function openCreateDialog() {
  selectedCategory.value = null
  formMode.value = 'create'
  formVisible.value = true
  categoryFieldErrors.value = {}
}

function openEditDialog(category) {
  selectedCategory.value = { ...category }
  formMode.value = 'edit'
  formVisible.value = true
  categoryFieldErrors.value = {}
}

function closeFormDialog() {
  formVisible.value = false
  selectedCategory.value = null
  categoryFieldErrors.value = {}
}

async function handleSubmitCategory(formData) {
  try {
    submittingCategory.value = true
    categoryFieldErrors.value = {}

    const payload = {
      name: formData.name,
      isActive: formData.isActive,
      parentId: formData.parentId,
      imageUrl: formData.imageUrl,
    }
    console.log('Отправка на сервер:', payload)

    if (formMode.value === 'create') {
      await createCategory(payload)
      toast.success('Категория создана')
    } else if (formMode.value === 'edit' && selectedCategory.value?.id) {
      await updateCategory(selectedCategory.value.id, payload)
      toast.success('Категория обновлена')
    }

    closeFormDialog()
    await loadCategories()
  } catch (error) {
    categoryFieldErrors.value = error.fieldErrors || {}
    toast.error(error.message || 'Ошибка при сохранении категории')
  } finally {
    submittingCategory.value = false
  }
}

function openDeleteDialog(category) {
  categoryToDelete.value = { ...category };
  deleteDialogVisible.value = true;
}

function closeDeleteDialog() {
  if (deletingCategory.value) return
  deleteDialogVisible.value = false;
  categoryToDelete.value = null;
}

async function handleDeleteCategory() {
  if (deletingCategory.value) return
  try {
    if (!categoryToDelete.value?.id) return;

    deletingCategory.value = true

    await deleteCategory(categoryToDelete.value.id)

    toast.success('Категория удалена')
    await loadCategories()
  } catch (error) {
    toast.error(error.message || 'Ошибка при удалении категории')
  } finally {
    deletingCategory.value = false
    closeDeleteDialog()
  }
}

const filteredCategories = computed(() => {
  const searchText = search.value.trim().toLowerCase();

  return categories.value.filter((category) => {
    const categoryName = category.name?.toLowerCase() ?? "";
    const parentName = getParentCategoryName(category.parentId).toLowerCase();

    return (
      !searchText ||
      categoryName.includes(searchText) ||
      parentName.includes(searchText)
    );
  });
});

onMounted(loadCategories);

const { currentPage, totalPages, paginatedItems: paginatedCategories } = usePagination(filteredCategories)
</script>

<template>
  <div class="admin-categories-page">
    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />

        <Button
          label="Создать категорию"
          icon="pi pi-plus"
          class="create-btn"
          @click="openCreateDialog"
        />
      </div>

      <div class="page-header">
        <div>
          <h1>Категории</h1>
          <p>Управление категориями товаров</p>
        </div>
      </div>

      <div class="filters">
        <div class="filter-group filter-group--search">
          <label>Поиск</label>
          <InputText
            v-model="search"
            placeholder="Поиск по названию категории"
            class="filter-control"
          />
        </div>
      </div>

      <Card class="categories-card">
        <template #content>
          <div v-if="loading" class="state-block">Загрузка...</div>

          <div v-else-if="categories.length === 0" class="state-block">
            Список категорий пуст
          </div>

          <div v-else-if="filteredCategories.length === 0" class="state-block">
            По вашему запросу ничего не найдено
          </div>

          <div v-else class="categories-grid">
            <div
              v-for="category in paginatedCategories"
              :key="category.id"
              class="category-card"
            >
              <div class="category-image-wrap">
                <img
                  v-if="category.imageUrl"
                  :src="category.imageUrl"
                  :alt="category.name"
                  class="category-image"
                />
                <div v-else class="category-image-placeholder">
                  <i class="pi pi-image"></i>
                </div>
              </div>

              <div class="category-content">
                <div class="category-header">
                  <div class="category-name">{{ category.name }}</div>
                </div>

                <div class="category-description">
                  Родительская категория:
                  <strong>{{
                    getParentCategoryName(category.parentId)
                  }}</strong>
                </div>

                <div class="category-meta">
                  <span
                    class="meta-chip"
                    :class="{
                      'meta-chip--active': category.isActive,
                      'meta-chip--inactive': !category.isActive,
                    }"
                  >
                    {{ category.isActive ? "Активна" : "Неактивна" }}
                  </span>

                  <span class="meta-chip">
                    {{
                      category.parentId
                        ? "Дочерняя категория"
                        : "Корневая категория"
                    }}
                  </span>
                </div>

                <div class="category-actions">
                  <Button
                    icon="pi pi-pencil"
                    class="icon-btn"
                    outlined
                    title="Редактировать"
                    @click="openEditDialog(category)"
                  />

                  <Button
                    icon="pi pi-trash"
                    class="icon-btn icon-btn--danger"
                    outlined
                    title="Удалить"
                    @click="openDeleteDialog(category)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-if="totalPages > 1" class="pagination">
            <Button icon="pi pi-chevron-left" outlined :disabled="currentPage === 1" @click="currentPage--" />
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <Button icon="pi pi-chevron-right" outlined :disabled="currentPage === totalPages" @click="currentPage++" />
          </div>
        </template>
      </Card>

      <CategoryFormDialog
        :visible="formVisible"
        :category="selectedCategory"
        :categories="categories"
        :mode="formMode"
        :submitting="submittingCategory"
        :field-errors="categoryFieldErrors"
        @close="closeFormDialog"
        @submit="handleSubmitCategory"
      />

      <DeleteCategoryConfirmDialog
        :visible="deleteDialogVisible"
        :category="categoryToDelete"
        :deleting="deletingCategory"
        @close="closeDeleteDialog"
        @confirm="handleDeleteCategory"
      />
    </div>
  </div>
</template>

<style scoped>
.admin-categories-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 1240px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 600;
  color: #444;
}

.filter-control {
  width: 100%;
}

.categories-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.categories-card.p-card) {
  background: #ffffff;
}

:deep(.categories-card .p-card-content) {
  padding: 24px;
}

.state-block {
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 20px 0 8px;
}

.page-info {
  font-size: 14px;
  color: #555;
  min-width: 60px;
  text-align: center;
}

.category-card {
  background: #ffffff;
  border: 1px solid #ececec;
  border-radius: 20px;
  display: flex;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(17, 17, 17, 0.08);
  border-color: #dddddd;
}

.category-image-wrap {
  width: 100px;
  min-width: 100px;
  background: #f5f5f5;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.category-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.category-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 32px;
}

.category-content {
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.category-name {
  font-weight: 700;
  font-size: 18px;
  color: #111111;
  letter-spacing: -0.02em;
  word-break: break-word;
}

.category-description {
  font-size: 13px;
  color: #6b6b6b;
  line-height: 1.5;
}

.category-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f6f6f6;
  border: 1px solid #ececec;
  color: #555555;
  font-size: 11px;
  font-weight: 500;
}

.meta-chip--active {
  background: #e8f5e9;
  border-color: #c8e6c9;
  color: #2e7d32;
}

.meta-chip--inactive {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #c62828;
}

.category-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.create-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.create-btn.p-button:hover) {
  background: #2a2a2a;
  border-color: #2a2a2a;
}

:deep(.icon-btn.p-button) {
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #111111;
  border-radius: 999px;
  width: 36px;
  height: 36px;
  box-shadow: none;
}

:deep(.icon-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.icon-btn.p-button .p-button-icon) {
  font-size: 14px;
}

:deep(.icon-btn--danger.p-button:hover) {
  border-color: #c41217;
  color: #c41217;
}

:deep(.p-inputtext) {
  border-radius: 14px;
  border-color: #ddd;
}

:deep(.p-inputtext:focus) {
  border-color: #111;
  box-shadow: 0 0 0 2px rgba(196, 18, 23, 0.08);
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .page-header h1 {
    font-size: 30px;
  }

  .categories-grid {
    grid-template-columns: 1fr;
  }

  .category-card {
    flex-direction: column;
  }

  .category-image-wrap {
    width: 100%;
    height: 150px;
  }
}

@media (max-width: 480px) {
  .page-shell {
    padding: 16px 12px 32px;
  }

  .page-header h1 {
    font-size: 26px;
  }

  .category-image-wrap {
    height: 120px;
  }

  .category-content {
    padding: 12px;
  }

  .category-name {
    font-size: 16px;
  }
}
</style>
